import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const FORMS = [
  { id: "player", tab: "Missing player", title: "Register A Player Or A First We Have Missed",
    blurb: "Kenyan football was never properly archived. If a player, a cap, a title or a landmark is missing from the record, send what you know — even if it is only a name and a club." },
  { id: "project", tab: "County project", title: "Tell Us About A County Project Or Facility",
    blurb: "Courts, pitches, academies, clubs and leagues are being built and proposed across the 47 counties. We map them, and we label funded and operating projects separately from proposed ones." },
  { id: "correction", tab: "Correction", title: "Correct Something We Have Published",
    blurb: "Every figure on this platform is published so it can be challenged. Point at the page, quote what is wrong, and tell us what it should say." },
  { id: "diaspora", tab: "Diaspora register", title: "Join The Diaspora Register",
    blurb: "This is not a donation appeal. The register records what Kenyans and friends of Kenya abroad can contribute — expertise, mentorship, scouting networks, partnership or capital — so we can match it to a county project. Your details stay private." },
  { id: "referral", tab: "Ground or stadium", title: "Tell Us About A Ground Or Stadium Project",
    blurb: "Tell us about a ground, a stand or a club project in your county so we can map it. This is not an introduction and we make none: we do not forward what you send to anyone, we take no part in any transaction, no fee or commission is offered or earned, and any licensed professional you or the owner needs is appointed by you, directly. Please do not send a plot or LR number, an owner's contact details, or anybody's budget." },
];

const EMPTY = {
  player: { player_name: "", years: "", clubs: "", county: "", country: "", achievement: "", evidence: "" },
  project: { project_name: "", county: "", project_type: "", project_status: "", organisation: "", cost_estimate: "", evidence: "" },
  correction: { page_url: "", statement: "", correction: "", evidence: "" },
  diaspora: { country_of_residence: "", county_of_origin: "", contributions: [], skills: "", availability: "", organisation: "", profile_link: "" },
  referral: { referral_type: "", subject_name: "", county: "", capacity: "", timeline: "", evidence: "", appoint_own_professional: false },
};

const SHARED = { your_name: "", your_email: "", your_role: "Football fan", notes: "" };

const FIELDS = {
  player: [
    ["player_name", "Player's name", true, "Full name"],
    ["years", "Years playing", false, "e.g. 1987 to 1996"],
    ["clubs", "Clubs", false, "In order, if you know them", "sm:col-span-2"],
    ["county", "County of origin", false, "e.g. Kakamega"],
    ["country", "Country or league", false, "e.g. Sweden, Allsvenskan"],
    ["achievement", "The first, cap or achievement", false, "What the record should show", "sm:col-span-2"],
    ["evidence", "Anything we can check it against", false, "A link, a newspaper, a name we can call", "sm:col-span-2"],
  ],
  project: [
    ["project_name", "Project or facility name", true, "e.g. Kisumu community futsal court"],
    ["county", "County", true, "e.g. Kisumu"],
    ["organisation", "Who is behind it", false, "County, club, NGO, private"],
    ["cost_estimate", "Cost, if known", false, "e.g. KSh 12m"],
    ["evidence", "Evidence or link", false, "Tender, news report, site photo, contact", "sm:col-span-2"],
  ],
  correction: [
    ["page_url", "Page or URL", true, "e.g. /money or the full link"],
    ["statement", "What we currently say", true, "Quote it as closely as you can"],
    ["correction", "What it should say", true, "The correct figure, name or date", "sm:col-span-2"],
    ["evidence", "Your source", false, "Published document, dataset, official statement", "sm:col-span-2"],
  ],
  diaspora: [
    ["country_of_residence", "Where you live now", true, "e.g. United Kingdom"],
    ["county_of_origin", "County you are connected to", false, "e.g. Siaya"],
    ["organisation", "Organisation, if any", false, "Club, company, association"],
    ["availability", "Time you could give", false, "e.g. a few hours a month"],
    ["skills", "Your expertise", false, "Coaching, law, finance, medicine, media, construction", "sm:col-span-2"],
    ["profile_link", "A profile or link", false, "LinkedIn, club page, portfolio", "sm:col-span-2"],
  ],
  referral: [
    ["subject_name", "Ground, project or organisation", true, "e.g. Kakamega County ground, or Talanta FC"],
    ["county", "County", false, "e.g. Kisumu"],
    ["capacity", "Size being considered", false, "e.g. 1,000 seat stand, 5,000 seat stadium"],
    ["timeline", "Timeline, if you know it", false, "e.g. before the 2027 season"],
    ["evidence", "Anything published we can check", false, "Tender notice, minutes, news report, link", "sm:col-span-2"],
  ],
};

const input =
  "mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-ochre focus:outline-none";
const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400";

/** Community-powered intake: four forms, one queue, nothing published without a human reading it. */
export default function Contribute() {
  const [params, setParams] = useSearchParams();
  const active = FORMS.find((f) => f.id === params.get("form")) || FORMS[0];
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ ...SHARED, ...EMPTY[active.id] });
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState("");
  const [emailed, setEmailed] = useState(true);

  useSeo({
    title: "Contribute — KickStartKenyaFootball",
    description:
      "Register a missing player, a county project or a correction, or join the diaspora register. Every submission is read by a person before anything is published.",
  });

  useEffect(() => {
    api.get("/intake/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    setForm({ ...SHARED, ...EMPTY[active.id] });
    setReference("");
  }, [active.id]);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleContribution = (value) =>
    setForm((f) => ({
      ...f,
      contributions: (f.contributions || []).includes(value)
        ? f.contributions.filter((c) => c !== value)
        : [...(f.contributions || []), value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/intake/${active.id}`, form);
      setReference(data.reference);
      setEmailed(Boolean(data.email_sent));
      toast.success(`Received — reference ${data.reference}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="contribute-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <p className="overline text-ochre">Community-powered information</p>
        <h1 className="display-1 mt-5 max-w-4xl">Help Us Build The Record</h1>
        <p className="body-copy mt-6 max-w-3xl">
          This platform is only as good as the information inside it, and no small team can see 47 counties at
          once. Five ways in, one queue, and a person reads every one. If you have a specific site or project,
          file it as a{" "}
          <Link to="/apply" className="text-ochre underline decoration-maasai underline-offset-4" data-testid="contribute-apply-link">
            preliminary opportunity submission
          </Link>{" "}
          instead — no funding, transaction or payment is promised, and telling us creates no entitlement to any
          payment or commission.
        </p>

        <div className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-ochre bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400" data-testid="contribute-policy">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-ochre" />
          <span>{meta?.review_policy || "Every submission is read by a person. AI helps us sort, score and spot duplicates; it never publishes anything. Diaspora registrations are held privately and are never published."}</span>
        </div>

        <div className="no-scrollbar mt-12 flex gap-1 overflow-x-auto border-b border-zinc-900" data-testid="contribute-tabs">
          {FORMS.map((f) => (
            <button
              key={f.id}
              onClick={() => setParams({ form: f.id })}
              data-testid={`contribute-tab-${f.id}`}
              className={`shrink-0 px-5 py-3 text-[0.66rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                active.id === f.id ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              {f.tab}
            </button>
          ))}
        </div>

        <Reveal key={active.id}>
          <h2 className="display-2 mt-12 max-w-3xl">{active.title}</h2>
          <p className="body-copy mt-5 max-w-3xl">{active.blurb}</p>

          {reference ? (
            <div className="mono mt-10 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-7 text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-ochre" data-testid="contribute-done">
              <p className="text-white">Reference {reference}</p>
              <p className="mt-4 text-zinc-400">
                {emailed
                  ? "Logged and acknowledged by email. Keep the reference for any follow-up."
                  : "Logged. We could not send the acknowledgement email just now, so write the reference down. Nothing is lost."}
                {" "}Nothing is published until we have verified it.
              </p>
              <button onClick={() => setReference("")} data-testid="contribute-again" className="mt-6 border border-ochre/40 px-4 py-2.5 text-ochre hover:bg-ochre hover:text-black">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 max-w-4xl" data-testid={`contribute-form-${active.id}`}>
              <div className="grid gap-5 sm:grid-cols-2">
                {FIELDS[active.id].map(([name, text, required, placeholder, span = ""]) => (
                  <label key={name} className={`block ${span}`}>
                    <span className={label}>
                      {text}
                      {required ? <span className="text-maasai"> *</span> : null}
                    </span>
                    <input
                      name={name}
                      value={form[name] ?? ""}
                      onChange={set}
                      required={required}
                      placeholder={placeholder}
                      data-testid={`intake-${name.replace(/_/g, "-")}`}
                      className={input}
                    />
                  </label>
                ))}

                {active.id === "project" ? (
                  <>
                    <label className="block">
                      <span className={label}>Type of project</span>
                      <select name="project_type" value={form.project_type} onChange={set} data-testid="intake-project-type" className={`${input} bg-black`}>
                        <option value="">Choose one</option>
                        {(meta?.project_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className={label}>Status on the ground</span>
                      <select name="project_status" value={form.project_status} onChange={set} data-testid="intake-project-status" className={`${input} bg-black`}>
                        <option value="">Choose one</option>
                        {(meta?.project_statuses || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                  </>
                ) : null}

                {active.id === "referral" ? (
                  <>
                    <label className="block">
                      <span className={label}>What are you telling us about</span>
                      <select name="referral_type" value={form.referral_type} onChange={set} data-testid="intake-referral-type" className={`${input} bg-black`}>
                        <option value="">Choose one</option>
                        {(meta?.referral_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-300 sm:col-span-2"
                      data-testid="intake-appoint-own-professional-label">
                      <input
                        type="checkbox"
                        name="appoint_own_professional"
                        checked={Boolean(form.appoint_own_professional)}
                        onChange={(e) => setForm((f) => ({ ...f, appoint_own_professional: e.target.checked }))}
                        required
                        data-testid="intake-appoint-own-professional"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-maasai"
                      />
                      <span>
                        I understand KickStartKenyaFootball makes no introductions, does not forward this to any
                        professional, owner or funder, and that I appoint any licensed professional myself,
                        directly. Nothing is promised and no payment or commission is earned by telling them.
                      </span>
                    </label>
                  </>
                ) : null}

                {active.id === "diaspora" ? (
                  <fieldset className="sm:col-span-2" data-testid="intake-contributions">
                    <legend className={label}>How you would like to contribute</legend>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(meta?.contributions || []).map((c) => {
                        const on = (form.contributions || []).includes(c);
                        return (
                          <button
                            type="button"
                            key={c}
                            onClick={() => toggleContribution(c)}
                            data-testid={`intake-contribution-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`mono border px-3.5 py-2 text-[0.58rem] uppercase tracking-[0.14em] transition-colors ${
                              on ? "border-ochre bg-ochre text-black" : "border-white/20 text-zinc-400 hover:border-ochre hover:text-ochre"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                ) : null}

                <label className="block">
                  <span className={label}>Your name<span className="text-maasai"> *</span></span>
                  <input name="your_name" value={form.your_name} onChange={set} required placeholder="So we can credit you" data-testid="intake-your-name" className={input} />
                </label>
                <label className="block">
                  <span className={label}>Your email<span className="text-maasai"> *</span></span>
                  <input type="email" name="your_email" value={form.your_email} onChange={set} required placeholder="Where we reply" data-testid="intake-your-email" className={input} />
                </label>
                <label className="block sm:col-span-2">
                  <span className={label}>You are</span>
                  <select name="your_role" value={form.your_role} onChange={set} data-testid="intake-your-role" className={`${input} bg-black`}>
                    {(meta?.roles || ["Football fan"]).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className={label}>Anything else we should know</span>
                  <textarea name="notes" rows={4} value={form.notes} onChange={set} data-testid="intake-notes" placeholder="Context, names, dates, what we should look up" className={input} />
                </label>
              </div>

              <button type="submit" disabled={busy} data-testid="intake-submit" className="btn-primary mt-8 inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Sending</> : "Send this in"}
              </button>
              <p className="mono mt-5 max-w-2xl text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
                {active.id === "diaspora"
                  ? "Held privately in the diaspora register. Never published, never sold, used only to match you to a project and to reply to you."
                  : active.id === "referral"
                  ? "Referrals are logged privately with a reference number and forwarded for a decision on applicability. Rewards are paid on completion, under the rules published on the modular stadiums page."
                  : "We hold your details only to reply to you and to check what you sent. You get a reference number by email. Where you tick the box above, we may publish the football fact you have given us — never your contact details."}
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
